import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Divider,
  Pagination,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Download as DownloadIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';

interface MovimientoEgreso {
  fecha: string;
  descripcion: string;
  referencia1?: string; // REFERENCIA 1
  referencia2?: string; // REFERENCIA 2
  valor: number;
  linea_original?: string;
}

interface MovimientosEgresoProps {
  archivoId?: string;
  archivoNombre?: string;
  onNavigate?: (path: string) => void;
}

export const MovimientosEgreso: React.FC<MovimientosEgresoProps> = ({ 
  archivoId, 
  archivoNombre,
  onNavigate 
}) => {
  const [movimientos, setMovimientos] = useState<MovimientoEgreso[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [archivoInfo, setArchivoInfo] = useState<{
    nombreArchivo: string;
    nombreReferencia: string;
    fechaSubida: string;
  } | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(5); // Número de elementos por página
  const [filterInfo, setFilterInfo] = useState<{
    movimientos_originales: number;
    movimientos_filtrados: number;
    movimientos_excluidos: number;
    descripciones_excluidas: string[];
  } | null>(null);

  useEffect(() => {
    if (archivoId) {
      cargarMovimientosDelPDF();
    }
  }, [archivoId, currentPage, itemsPerPage]);

  const cargarMovimientosDelPDF = async () => {
    if (!archivoId) return;
    
    setLoading(true);
    setError(null);

    try {
      // 1. Obtener información del archivo
      const infoResponse = await fetch(`http://localhost:3002/api/resumenes/${archivoId}`);
      if (!infoResponse.ok) {
        throw new Error('Error al obtener información del archivo');
      }
      const infoData = await infoResponse.json();
      setArchivoInfo(infoData.data);

      // 2. Procesar el PDF y extraer movimientos negativos (con paginación)
      const movimientosResponse = await fetch(`http://localhost:3002/api/resumenes/${archivoId}/movimientos-egreso?page=${currentPage}&limit=${itemsPerPage}`);
      if (!movimientosResponse.ok) {
        throw new Error('Error al procesar el archivo PDF');
      }
      const movimientosData = await movimientosResponse.json();
      setMovimientos(movimientosData.data || []);
      
      // Actualizar información de paginación
      if (movimientosData.pagination) {
        setTotalPages(movimientosData.pagination.total_pages);
        setTotalItems(movimientosData.pagination.total_items);
      }
      
      // Actualizar información de filtrado
      if (movimientosData.filter_info) {
        setFilterInfo(movimientosData.filter_info);
      }

    } catch (error) {
      console.error('Error al cargar movimientos:', error);
      setError(error instanceof Error ? error.message : 'Error desconocido');
      // No mostrar datos dummy cuando hay error - mostrar el error real
      setMovimientos([]);
      // No establecer información dummy del archivo cuando hay error
      setArchivoInfo(null);
    } finally {
      setLoading(false);
    }
  };

  const formatearValor = (valor: number) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(Math.abs(valor));
  };

  const formatearFecha = (fecha: string) => {
    if (!fecha || fecha === 'N/A' || fecha === 'ERROR') {
      return fecha || '-';
    }

    // Intentar diferentes formatos de fecha
    let fechaObj: Date | null = null;
    
    // Formato DD/MM/YYYY
    if (fecha.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
      const [dia, mes, año] = fecha.split('/');
      fechaObj = new Date(parseInt(año), parseInt(mes) - 1, parseInt(dia));
    }
    // Formato YYYY/MM/DD  
    else if (fecha.match(/^\d{4}\/\d{2}\/\d{2}$/)) {
      const [año, mes, dia] = fecha.split('/');
      fechaObj = new Date(parseInt(año), parseInt(mes) - 1, parseInt(dia));
    }
    // Formato ISO o estándar
    else {
      fechaObj = new Date(fecha);
    }

    // Verificar si la fecha es válida
    if (fechaObj && !isNaN(fechaObj.getTime())) {
      return fechaObj.toLocaleDateString('es-CO', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } else {
      return fecha; // Retornar la fecha original si no se puede parsear
    }
  };

  // Eliminada función getTipoColor - ya no necesitamos tipos

  const totalEgresos = movimientos.reduce((sum, mov) => sum + Math.abs(mov.valor), 0);

  const handleVolver = () => {
    if (onNavigate) {
      onNavigate('/contabilidad/informe-bancario');
    }
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (event: any) => {
    const newItemsPerPage = parseInt(event.target.value);
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Resetear a la primera página cuando cambie el número de items
  };

  const handleCrearComprobante = () => {
    // TODO: Implementar creación de comprobante con los movimientos seleccionados
    alert('Funcionalidad de creación de comprobante en desarrollo');
  };

  return (
    <Box sx={{ padding: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleVolver}
            variant="outlined"
          >
            Volver a Informes
          </Button>
          <Typography variant="h4">
            Movimientos de Egreso
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<ReceiptIcon />}
          onClick={handleCrearComprobante}
          disabled={movimientos.length === 0}
        >
          Crear Comprobante
        </Button>
      </Box>

      {/* Información del archivo */}
      {archivoInfo && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Información del Archivo
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
              <Typography variant="body2">
                <strong>Archivo:</strong> {archivoInfo.nombreArchivo}
              </Typography>
              <Typography variant="body2">
                <strong>Referencia:</strong> {archivoInfo.nombreReferencia}
              </Typography>
              <Typography variant="body2">
                <strong>Fecha de subida:</strong> {formatearFecha(archivoInfo.fechaSubida)}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Resumen */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Resumen de Movimientos
          </Typography>
          <Box sx={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Total de Egresos
              </Typography>
              <Typography variant="h5" color="error.main">
                {formatearValor(totalEgresos)}
              </Typography>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box>
              <Typography variant="body2" color="text.secondary">
                Movimientos Actuales
              </Typography>
              <Typography variant="h5">
                {movimientos.length}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Página {currentPage} de {totalPages} | Total: {totalItems}
              </Typography>
            </Box>
            {filterInfo && filterInfo.movimientos_excluidos > 0 && (
              <>
                <Divider orientation="vertical" flexItem />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Filtros Aplicados
                  </Typography>
                  <Typography variant="h5" color="warning.main">
                    {filterInfo.movimientos_excluidos}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Movimientos excluidos
                  </Typography>
                </Box>
              </>
            )}
          </Box>
          
          {/* Información de filtrado detallada */}
          {filterInfo && filterInfo.movimientos_excluidos > 0 && (
            <Box sx={{ mt: 2, p: 2, backgroundColor: 'warning.light', borderRadius: 1 }}>
              <Typography variant="body2" color="warning.dark" sx={{ fontWeight: 'medium' }}>
                📋 Filtros automáticos aplicados:
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Se excluyeron {filterInfo.movimientos_excluidos} movimientos de {filterInfo.movimientos_originales} 
                encontrados (comisiones, impuestos automáticos y cargos administrativos).
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>
            Procesando archivo PDF...
          </Typography>
        </Box>
      )}

      {/* Error */}
      {error && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Tabla de movimientos */}
      {!loading && movimientos.length > 0 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'grey.50' }}>
                <TableCell><strong>Fecha</strong></TableCell>
                <TableCell><strong>Descripción</strong></TableCell>
                <TableCell><strong>Referencia 1</strong></TableCell>
                <TableCell><strong>Referencia 2</strong></TableCell>
                <TableCell align="right"><strong>Valor</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {movimientos.map((movimiento, index) => (
                <TableRow key={index} hover>
                  <TableCell>
                    <Typography variant="body2">
                      {formatearFecha(movimiento.fecha)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {movimiento.descripcion}
                    </Typography>
                    {movimiento.linea_original && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        Línea original: {movimiento.linea_original}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="primary.main" fontWeight="medium">
                      {movimiento.referencia1 || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="secondary.main" fontWeight="medium">
                      {movimiento.referencia2 || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography color="error.main" fontWeight="bold" variant="body1">
                      -{formatearValor(movimiento.valor)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Paginación */}
      {!loading && totalItems > 0 && (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
          <Stack spacing={2} alignItems="center">
            {/* Selector de items por página */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Registros por página:
              </Typography>
              <FormControl size="small" sx={{ minWidth: 80 }}>
                <Select
                  value={itemsPerPage}
                  onChange={handleItemsPerPageChange}
                  displayEmpty
                  inputProps={{ 'aria-label': 'Registros por página' }}
                >
                  <MenuItem value={5}>5</MenuItem>
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={15}>15</MenuItem>
                  <MenuItem value={20}>20</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                </Select>
              </FormControl>
            </Box>
            
            {/* Paginación solo si hay más de una página */}
            {totalPages > 1 && (
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
              />
            )}
            
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Mostrando {movimientos.length} de {totalItems} movimientos
              {totalPages > 1 && (
                <> | Página {currentPage} de {totalPages}</>
              )}
            </Typography>
          </Stack>
        </Box>
      )}

      {/* Sin movimientos */}
      {!loading && !error && movimientos.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            No se encontraron movimientos de egreso en este archivo
          </Typography>
        </Paper>
      )}
    </Box>
  );
};
